import { replayAt } from './replayEngine.js';

export const createReplaySession = ({
  index, audioClock = null, onUpdate = () => {},
  now = () => performance.now(),
  initialTimeMs = 0,
}) => {
  if (!index?.bundle || !Array.isArray(index.bundle.events)) {
    throw new TypeError('Replay session requires a validated index');
  }
  const durationMs = index.bundle.durationMs;
  const clamp = (ms) => Math.max(0, Math.min(durationMs, Math.floor(ms)));
  let currentTimeMs = clamp(initialTimeMs);
  let running = false;
  let disposed = false;
  let anchorMs = currentTimeMs;
  let anchorClock = now();
  let speed = 1;

  const positionAt = (timestamp) => running
    ? clamp(anchorMs + (timestamp - anchorClock) * speed)
    : currentTimeMs;

  const emit = () => {
    if (disposed) return null;
    const replay = replayAt(index, currentTimeMs);
    const result = { timeMs: currentTimeMs, durationMs,
      playing: running, speed, replay };
    onUpdate(result);
    // Sound never controls the game clock. Missing audio is an explicit gap.
    if (audioClock) {
      Promise.resolve(audioClock.sync(currentTimeMs, running, speed))
        .catch(() => {}); // the audio clock handles its own playback errors
    }
    return result;
  };

  return {
    play() {
      if (disposed) return;
      if (currentTimeMs >= durationMs) currentTimeMs = 0;
      anchorMs = currentTimeMs;
      anchorClock = now();
      running = true;
      return emit();
    },
    pause() {
      if (disposed) return;
      currentTimeMs = positionAt(now());
      running = false;
      return emit();
    },
    seek(ms) {
      if (disposed) return;
      if (!Number.isFinite(ms)) throw new TypeError('Invalid replay seek');
      currentTimeMs = clamp(ms);
      anchorMs = currentTimeMs;
      anchorClock = now();
      if (currentTimeMs >= durationMs) running = false;
      return emit();
    },
    setSpeed(next) {
      if (disposed) return;
      if (![0.5, 0.75, 1, 1.25, 1.5, 2].includes(next)) {
        throw new RangeError('Unsupported replay speed');
      }
      currentTimeMs = positionAt(now());
      anchorMs = currentTimeMs;
      anchorClock = now();
      speed = next;
      return emit();
    },
    tick() {
      if (disposed) return null;
      currentTimeMs = positionAt(now());
      if (currentTimeMs >= durationMs && running) running = false;
      return emit();
    },
    snapshot() {
      return { timeMs: currentTimeMs, durationMs, playing: running,
        speed, replay: replayAt(index, currentTimeMs) };
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      running = false;
      audioClock?.dispose?.();
    },
  };
};
